import React, { useCallback } from 'react';

import { Cookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';

// bonnet
import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';
import { formatCurrency } from '@atc/string-fns';

import { sendClick } from 'reaxl-analytics';
import { ShareContent } from 'reaxl-organisms';

import { vdpBaseUrlBuilder } from '@/utilities/vdpUrlBuilder';

import { shareClick } from '@/analytics/inventoryAnalyticsHandlers';

import authDuck from '@/ducks/authDuck.js';
import currentPageNameDuck from '@/ducks/currentPageNameDuck.js';
import shareEmailSubmissionDuck from '@/ducks/shareEmailSubmissionDuck.js';
import shareTextSubmissionDuck from '@/ducks/shareTextSubmissionDuck.js';
import srpActiveEmailListingDuck from '@/ducks/srp/srpActiveEmailListingDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import userDuck from '@/ducks/userDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

export const ShareContentContainer = React.memo(({
    glyphColor,
    isStaticContent = false,
    uiContext = 'default',
    className = '',
    ctaType = '',
}) => {
    const {
        brand: [, {
            leadsPartnerName,
            brandName,
        }],
        share: [, {
            lnxEmail,
            lnxText,
            mobileSmsUrl,
            vdpLink,
            use_short_link: useShortLink = true,
        }],
    } = useFeatures(['brand', 'share']);

    const device = useDevice();
    const dispatch = useDispatch();

    const currentPageName = useSelector(currentPageNameDuck.selectors.getDuckState) || '';

    const srpListing = useSelector(srpActiveEmailListingDuck.selectors.getActiveInventory)?.[0];
    const vdpListing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const inventoryListing = ((currentPageName && currentPageName.toString() === 'srp') ? srpListing : vdpListing) || {};

    const emailSubmissionStatus = useSelector(shareEmailSubmissionDuck.selectors.getEmailSubmissionStatus);
    const textSubmissionStatus = useSelector(shareTextSubmissionDuck.selectors.getTextSubmissionStatus);

    const userZip = useSelector(userDuck.selectors.getZip);
    const { firstName = '', lastName = '', email = '' } = useSelector(userDuck.selectors.getUserProfile);

    const { pricingDetail = {}, year, make, model, listingType, id: listingId, owner: { id: ownerId } = {}, type } = inventoryListing;
    const condition = listingType || type;

    const price = pricingDetail?.incentive || pricingDetail?.salePrice || pricingDetail?.msrp || 0;
    const priceClause = price ? `listed for ${formatCurrency(price)} ` : '';
    const emailFriendVdpUrl = vdpBaseUrlBuilder(vdpLink, {
        query: {
            listingId,
            LNX: lnxEmail,
        },
    });
    let listingIdTextValue = listingId || '';
    if (listingIdTextValue && useShortLink) {
        listingIdTextValue = listingIdTextValue.toString(16);
    }
    const emailFriendMessage = `Check out this ${year} ${make?.name} ${model?.name} ${priceClause}on ${brandName}`;
    const textMessage = ` shared a ${condition} ${year} ${make?.name} ${model?.name} ${priceClause}with you on ${brandName}. ${mobileSmsUrl}`
        + (lnxText ? `${lnxText}/` : '') + listingIdTextValue;
    const disclaimer = `Text alerts or calls about the notifications you have chosen to receive may be sent using an autodialer
            or prerecorded voice, but you can opt out at any time by replying STOP. Of course, consent is not required to receive any services
            from ${brandName}. Message and data rates may apply.`;

    const cookies = new Cookies();
    const consumerIP = cookies.get('pxa_ipv4');
    const cookieId = cookies.get('ATC_ID');

    const isLink = uiContext === 'link';

    const isNba = ctaType === 'nba';

    const parentId = useSelector(srpActiveInteractionDuck.selectors.getParentId) || undefined;
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);

    const handleShareClick = useCallback((shareType, event = { type: 'click' }) => {
        if (currentPageName === 'srp') {
            dispatch(srpActiveEmailListingDuck.creators.setActiveResults([inventoryListing?.id]));
        }
        sendClick(shareClick, event, { type: shareType, id: inventoryListing?.id, par: parentId });
    }, [currentPageName, inventoryListing?.id, parentId, dispatch]);

    const emailSubmit = useCallback((data) => {
        dispatch(shareEmailSubmissionDuck.creators.emailFriend(data));
        handleShareClick('email');
    }, [dispatch, handleShareClick]);

    const messageSubmit = useCallback((data) => {
        dispatch(shareTextSubmissionDuck.creators.textFriend(data));
        handleShareClick('text');
    }, [dispatch, handleShareClick]);

    const resetEmail = useCallback(() => {
        dispatch(shareEmailSubmissionDuck.creators.reset());
    }, [dispatch]);

    const resetText = useCallback(() => {
        dispatch(shareTextSubmissionDuck.creators.reset());
    }, [dispatch]);

    const emailProps = {
        listingId,
        ownerId,
        shareLink: emailFriendVdpUrl,
        message: emailFriendMessage,
        onSubmit: emailSubmit,
        serverError: emailSubmissionStatus?.serverError,
        showConfirmation: emailSubmissionStatus?.showConfirmation,
        resetEmailFriend: resetEmail,
        ip: consumerIP,
        originPage: 'FYC_VDP_EMAIL_A_FRIEND',
        searchZip: userZip,
        partnerName: leadsPartnerName,
        lnxCode: lnxEmail,
        cookieId,
        myAtcUserLoggedIn: isLoggedIn,
        firstName,
        lastName,
        email,
    };

    const textProps = {
        message: textMessage.toString(),
        listingId,
        serverError: textSubmissionStatus?.serverError,
        showConfirmation: textSubmissionStatus?.showConfirmation,
        onSubmit: messageSubmit,
        onTabClick: () => handleShareClick('text'),
        disclaimer,
        userName: firstName,
    };

    const title = 'Share this car';

    let buttonText;

    if (isLink) {
        buttonText = 'Share this vehicle';
    }

    return (
        <ShareContent
            data-cmp="shareThisCar"
            title={title}
            emailProps={emailProps}
            glyphColor={glyphColor}
            textProps={textProps}
            isMobile={device?.type === 'phone'}
            isStaticContent={isStaticContent}
            resetTextFriend={resetText}
            resetEmailFriend={resetEmail}
            onClick={() => handleShareClick(isNba ? 'nba' : 'icon', title)}
            uiContext={uiContext}
            buttonText={buttonText}
            className={className}
        />
    );
});

export default ShareContentContainer;
